import { ReactNode, useMemo } from 'react';
import { FileError, FileRejection, useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

import { config } from '../../config';
import { locator } from '../../testing/e2e-locator/locator';

type FileExists = (file: File) => boolean;

type DropzoneProps = {
  isDuplicate: FileExists;
  onDrop: (acceptedFiles: File[], rejections: FileRejection[]) => void;
};

const Dropzone = ({ isDuplicate, onDrop }: DropzoneProps): ReactNode => {
  const { t } = useTranslation();

  const validator = (file: File | DataTransferItem): FileError | null => {
    if (file instanceof DataTransferItem) {
      return null;
    }

    return isDuplicate(file)
      ? {
          code: 'file-already-selected',
          message: t('dropzone.rejectedFileDuplicate'),
        }
      : null;
  };

  const baseStyle = {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderColor: 'var(--radio-border-color)',
    borderRadius: 2,
    borderStyle: 'dashed',
    borderWidth: 2,
    color: 'var(--label-primary-color)',
    display: 'flex',
    flex: 1,
    flexDirection: 'column' as const,
    fontWeight: 700,
    margin: '0 0 3rem 0',
    outline: 'none',
    padding: '5rem',
    transition: 'border .24s ease-in-out',
  };

  const focusedStyle = {
    borderColor: '#2196f3',
  };

  const acceptStyle = {
    borderColor: 'var(--primary-color)',
    backgroundColor: 'var(--page-background-color)',
  };

  const rejectStyle = {
    borderColor: '#ff1744',
  };

  const {
    getInputProps,
    getRootProps,
    isDragAccept,
    isDragActive,
    isDragReject,
    isFocused,
  } = useDropzone({
    onDrop,
    validator,
    ...config.loanDocuments.dropzone,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  return (
    <div
      data-testid={locator.components.dropzone.root}
      {...getRootProps({ style })}
    >
      <input
        data-testid={locator.components.dropzone.fileInput}
        {...getInputProps({
          required: true,
          name: 'file-input',
          'aria-label': t('dropzone.fileInput'),
        })}
      />
      {isDragActive ? (
        <p data-testid={locator.components.dropzone.activeText}>
          {isDragReject ? t('dropzone.invalid') : t('dropzone.active')}
        </p>
      ) : (
        <p>
          <Trans
            components={[
              <b
                data-testid={locator.components.dropzone.inactiveText}
                key={'dropzone-b'}
                style={{
                  color: 'var(--primary-color)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  textDecoration: 'underline',
                }}
              />,
            ]}
            i18nKey={'dropzone.inactive'}
          />
        </p>
      )}
    </div>
  );
};

export default Dropzone;
